import type { SdkMixin } from '@wix/lean-components-infra';
import {
  disablePropsSDKFactory,
  changePropsSDKFactory,
  focusPropsSDKFactory,
  composeSDKFactories,
  toJSONBase,
  messageTemplates,
  reportWarning,
  reportError,
  createAccessibilityPropSDKFactory,
} from '@wix/editor-elements-corvid-utils';
import type { ValidationSchema } from '@wix/editor-elements-corvid-utils';
import { createComponentSDKModel } from '@wix/editor-elements-integrations/corvid';
import { createLeanComponentSDK } from '@wix/lean-components-infra/runtime/sdk';
import type { CorvidTypes } from '@wix/editor-elements-types/corvid';
import type { IRangeSliderProps } from '../RangeSlider.props';

const continuousStepValue = 0.1;
const valueArraySize = 2;

export const SliderTypes = {
  Stepped: 'Stepped',
  Continuous: 'Continuous',
} as const;
export type SliderType = keyof typeof SliderTypes;

export const StepValueTypes = {
  Number: 'Number',
  Value: 'Value',
} as const;
export type StepValueType = keyof typeof StepValueTypes;

const validateNumberValue = (
  newMinValue: number,
  newMaxValue: number,
  min: number,
  max: number,
) => {
  if (newMinValue > newMaxValue) {
    reportWarning(
      messageTemplates.warning_at_most({
        propertyName: 'value[0]',
        wrongValue: newMinValue,
        maxValue: newMaxValue,
      }),
    );
    return false;
  }
  if (newMinValue < min) {
    reportWarning(
      messageTemplates.warning_at_least({
        propertyName: 'value',
        wrongValue: newMinValue,
        minValue: min,
      }),
    );
    return false;
  }
  if (newMaxValue > max) {
    reportWarning(
      messageTemplates.warning_at_most({
        propertyName: 'value',
        wrongValue: newMaxValue,
        maxValue: max,
      }),
    );
    return false;
  }
  return true;
};

const isNumber = (num: any) => !isNaN(Number(num)) && num !== null;

const sdkSchema = {
  label: { type: 'string' },
  sliderType: { type: 'string' },
  stepType: { type: 'string' },
  step: { type: 'number' },
  min: { type: 'number' },
  max: { type: 'number' },
  prefix: { type: 'string' },
  suffix: { type: 'string' },
};

const validationSchema: ValidationSchema = {
  type: ['object'],
  properties: {
    label: { maxLength: 120, type: ['string'] },
    sliderType: { type: ['string'], enum: ['Continuous', 'Stepped'] },
    stepType: { type: ['string'], enum: ['Number', 'Value'] },
    step: { type: ['number'] },
    min: { type: ['number'] },
    max: { type: ['number'] },
    value: { type: ['array', 'number'] },
    prefix: { maxLength: 120, type: ['string'] },
    suffix: { maxLength: 120, type: ['string'] },
  },
};

const mixins = ['changeable'] as Array<SdkMixin>;

export const leanSdk = createLeanComponentSDK<IRangeSliderProps>({
  sdkSchema,
  validationSchema,
  mixins,
});

const _sliderSDKFactory: CorvidTypes.CorvidSDKFactory<
  IRangeSliderProps,
  any
> = ({ setProps, props, metaData }) => {
  const sdkProps = {
    get sliderType() {
      return props.sliderType;
    },
    set sliderType(value) {
      if (value in SliderTypes) {
        setProps({ sliderType: value });
      } else {
        reportError(
          messageTemplates.error_bad_format_with_hint({
            propertyName: 'sliderType',
            functionName: 'set sliderType',
            wrongValue: value.toString(),
            hint: `Stepped or Continuous`,
          }),
        );
      }
    },
    get step() {
      if (props.sliderType === SliderTypes.Stepped) {
        return props.step;
      }
      return continuousStepValue;
    },
    set step(value) {
      if (props.sliderType === SliderTypes.Continuous) {
        reportError(
          messageTemplates.error_bad_format_with_hint({
            propertyName: 'step',
            functionName: 'set step',
            wrongValue: value.toString(),
            hint: 'Continuous slider does not support setting step',
          }),
        );
      } else {
        if (value < props.max - props.min) {
          setProps({ step: value });
        } else {
          reportError(
            messageTemplates.error_less_than({
              propertyName: 'step',
              functionName: 'set step',
              value,
              maximum: props.max - props.min,
            }),
          );
        }
      }
    },
    get stepType() {
      return props.stepType;
    },
    set stepType(value) {
      if (props.sliderType === SliderTypes.Continuous) {
        reportError(
          messageTemplates.error_unsupported_property_with_hint({
            propertyName: 'stepType',
            hint: 'slider type is set to Continuous',
          }),
        );
        return;
      }
      if (value !== undefined && value in StepValueTypes) {
        setProps({ stepType: value });
      } else {
        reportError(
          messageTemplates.error_unsupported_property_with_hint({
            propertyName: 'stepType',
            hint: `slider type is set to Continuous or value must be Number or Value`,
          }),
        );
      }
    },
    get min() {
      return props.min;
    },
    set min(value) {
      if (value > props.max && value > props.minValue) {
        reportWarning(
          messageTemplates.warning_at_most({
            propertyName: 'min',
            wrongValue: value,
            maxValue: props.max,
          }),
        );
      }
      setProps({ min: value });
    },
    get max() {
      return props.max;
    },
    set max(value) {
      if (value < props.min && value < props.maxValue) {
        reportWarning(
          messageTemplates.warning_at_least({
            propertyName: 'max',
            wrongValue: value,
            minValue: props.min,
          }),
        );
      }
      setProps({ max: value });
    },
    get value() {
      if (props.minValue === props.maxValue) {
        return props.minValue;
      }
      return new Proxy([props.minValue, props.maxValue], {
        set: (_, key, value) => {
          if (key === `0` && isNumber(value)) {
            setProps({ minValue: value });
            return true;
          }
          if (key === `1` && isNumber(value)) {
            setProps({ maxValue: value });
            return true;
          }
          return false;
        },
      });
    },
    set value(newValue) {
      if (typeof newValue === 'number') {
        if (validateNumberValue(newValue, newValue, props.min, props.max)) {
          setProps({ minValue: newValue, maxValue: newValue });
        }
        return;
      } else {
        if (
          Array.isArray(newValue) &&
          newValue.every(isNumber) &&
          newValue.length === valueArraySize
        ) {
          const [newMinValue, newMaxValue] = newValue;
          if (
            validateNumberValue(newMinValue, newMaxValue, props.min, props.max)
          ) {
            setProps({ minValue: newMinValue, maxValue: newMaxValue });
          }
          return;
        }
        reportWarning(
          messageTemplates.warning_invalid_option({
            propertyName: 'value',
            wrongValue: newValue,
            index: 0,
          }),
        );
      }
    },
    get prefix() {
      return props.sliderType === SliderTypes.Stepped
        ? props.prefix
        : undefined;
    },
    set prefix(value) {
      if (props.sliderType === SliderTypes.Stepped) {
        setProps({ prefix: value });
      } else {
        reportError(
          messageTemplates.error_unsupported_property_with_hint({
            propertyName: 'value',
            hint: 'slider type is set to Continuous',
          }),
        );
      }
    },
    get suffix() {
      return props.sliderType === SliderTypes.Stepped
        ? props.suffix
        : undefined;
    },
    set suffix(value) {
      if (props.sliderType === SliderTypes.Stepped) {
        setProps({ suffix: value });
      } else {
        reportError(
          messageTemplates.error_unsupported_property_with_hint({
            propertyName: 'value',
            hint: 'slider type is set to Continuous',
          }),
        );
      }
    },
    toJSON() {
      const { sliderType, stepType, step, min, max, value, prefix, suffix } =
        sdkProps;
      return {
        ...toJSONBase(metaData),
        sliderType,
        stepType,
        step,
        min,
        max,
        value,
        prefix,
        suffix,
      };
    },
  };
  return sdkProps;
};

const accessibilityPropsSDKFactory = createAccessibilityPropSDKFactory({
  enableLang: true,
});

export const sdk = composeSDKFactories<IRangeSliderProps, any>([
  leanSdk,
  _sliderSDKFactory,
  disablePropsSDKFactory,
  changePropsSDKFactory,
  focusPropsSDKFactory,
  accessibilityPropsSDKFactory,
]);

export default createComponentSDKModel(sdk);
